import type { QueryClient } from '@tanstack/react-query';
import i18next from 'i18next';

import { userSettingsQueryOptions } from '@/lib/firebase/hooks/use-user-settings-query-options';
import { subscribeUserSettings, type UserSettings, updateUserSettings } from '@/lib/firebase/store';
import { resolveLocale } from '@/lib/i18n';

/**
 * Reconciles the remote language with i18next on the first snapshot only — a later remote change
 * must not yank the language mid-session. Subsequent snapshots still refresh the query cache.
 */
export const initializeUserSettingsObserver = (queryClient: QueryClient, userId: string, onError?: (error: Error) => void) => {
  let isSnapshotReceived = false;

  const handleSnapshot = (data: UserSettings | null) => {
    isSnapshotReceived = true;

    if (!data) return;

    if (!data.language) {
      // Resolve before writing — the detector can yield region tags (e.g. 'de-AT') and the shared
      // document should only ever hold bare locale codes.
      void updateUserSettings(userId, { language: resolveLocale(i18next.language) }).catch((error) => onError?.(error));
    } else {
      // The shared document can hold values outside this app's locales (region tags, future languages).
      const locale = resolveLocale(data.language);
      if (locale !== i18next.language) void i18next.changeLanguage(locale);
    }
  };

  const unsubscribe = subscribeUserSettings(
    userId,
    (data) => {
      queryClient.setQueryData<UserSettings | null>(userSettingsQueryOptions(userId).queryKey, data);
      if (!isSnapshotReceived) handleSnapshot(data);
    },
    (error) => {
      onError?.(error);
    },
  );

  return () => {
    unsubscribe();
  };
};
